'use client';

import { useState, useEffect } from 'react';
import { getConsent, setConsent, type ConsentState } from '@/lib/consent';
import { Shield, ChevronDown, ChevronUp } from 'lucide-react';

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    // Only show banner if no consent decision has been made
    const existing = getConsent();
    if (!existing) {
      setVisible(true);
    }
  }, []);

  function handleAcceptAll() {
    const state: ConsentState = { essential: true, analytics: true };
    setConsent(state);
    setVisible(false);
    // Reload to allow Sentry to initialise with consent
    window.location.reload();
  }

  function handleAcceptSelected() {
    const state: ConsentState = { essential: true, analytics };
    setConsent(state);
    setVisible(false);
    if (analytics) {
      // Reload to allow Sentry to initialise with consent
      window.location.reload();
    }
  }

  function handleRejectAll() {
    const state: ConsentState = { essential: true, analytics: false };
    setConsent(state);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-[9999] p-4 sm:p-6 pointer-events-none">
      <div className="pointer-events-auto mx-auto max-w-lg rounded-xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10">
        <div className="p-5">
          {/* Header */}
          <div className="flex items-start gap-3 mb-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50">
              <Shield className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Cookie Preferences</h3>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                We use essential cookies to keep the app working and optional analytics cookies
                (Sentry) to monitor errors and improve reliability. No marketing cookies are used.
              </p>
            </div>
          </div>

          {/* Details toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 mb-3"
          >
            {showDetails ? 'Hide details' : 'Show details'}
            {showDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>

          {showDetails && (
            <div className="mb-4 space-y-3 rounded-lg border border-slate-100 bg-slate-50 p-3">
              {/* Essential */}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium text-slate-700">Essential</p>
                  <p className="text-[11px] text-slate-500">Authentication, session management. Always active.</p>
                </div>
                <div className="flex h-5 items-center">
                  <input
                    type="checkbox"
                    checked
                    disabled
                    className="h-4 w-4 rounded border-slate-300 text-blue-600"
                  />
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium text-slate-700">Analytics &amp; Error Tracking</p>
                  <p className="text-[11px] text-slate-500">
                    Sentry error monitoring and session replay to identify and fix issues.
                    No PII is collected.
                  </p>
                </div>
                <div className="flex h-5 items-center">
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={e => setAnalytics(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2">
            <button
              onClick={handleRejectAll}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Essential only
            </button>

            {showDetails && (
              <button
                onClick={handleAcceptSelected}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Save preferences
              </button>
            )}

            <button
              onClick={handleAcceptAll}
              className="rounded-lg bg-blue-600 px-4 py-2 text-xs font-medium text-white hover:bg-blue-700 transition-colors sm:ml-auto"
            >
              Accept all
            </button>
          </div>

          {/* Privacy link */}
          <p className="mt-3 text-center text-[10px] text-slate-400">
            Read our{' '}
            <a href="/privacy" className="underline hover:text-slate-600">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
